/**
 * Phantom Screens Complete Dataset
 *
 * Comprehensive data for all Phantom Screens dealers, metrics, and historical data.
 * This data structure serves:
 * 1. Dashboard UI with expandable dealer lists
 * 2. SLM training for AI-powered insights
 * 3. Historical trend analysis (1992-present)
 *
 * Founded: 1992 in Abbotsford, BC (co-founded with Rooke family connection)
 */

export type TimePeriod = 'DoD' | 'WoW' | 'MoM' | 'QoQ' | 'YoY';

export interface TimeSeriesValue {
  current: number;
  DoD: number;
  WoW: number;
  MoM: number;
  QoQ: number;
  YoY: number;
}

export interface Dealer {
  id: string;
  name: string;
  address: string;
  city: string;
  stateProvince: string;
  postalCode: string;
  region: string;
  dealerSince: string;
  revenue: TimeSeriesValue;
  unitsInstalled: TimeSeriesValue;
  avgOrderValue: TimeSeriesValue;
  otifRate: number; // On-Time In-Full %
  customerSatisfaction: number;
  certificationLevel: 'Gold' | 'Silver' | 'Bronze' | 'Standard';
  isTopPerformer: boolean;
  isBottomPerformer: boolean;
}

export interface RegionalData {
  region: string;
  abbreviation: string;
  dealerCount: number;
  revenue: TimeSeriesValue;
  unitsInstalled: TimeSeriesValue;
  otifRate: number;
  dealers: Dealer[];
}

// Generate realistic dealer data
const generateDealers = (region: string, abbrev: string, cities: string[], count: number): Dealer[] => {
  const dealers: Dealer[] = [];
  const businessTypes = ['Screens', 'Home Solutions', 'Outdoor Living', 'Installations', 'Renovations', 'Home Improvement', 'Screen Systems', 'Window & Door'];
  const certLevels: ('Gold' | 'Silver' | 'Bronze' | 'Standard')[] = ['Gold', 'Silver', 'Bronze', 'Standard'];

  for (let i = 0; i < count; i++) {
    const city = cities[i % cities.length];
    const businessType = businessTypes[i % businessTypes.length];
    const yearJoined = 1992 + Math.floor(Math.random() * 32);
    const baseRevenue = 500000 + Math.random() * 2000000;
    const isTop = i < 3;
    const isBottom = i >= count - 2;

    dealers.push({
      id: `PHS-${abbrev}-${String(i + 1).padStart(3, '0')}`,
      name: `${city} ${businessType}`,
      address: `${100 + i * 50} ${businessType} Avenue`,
      city,
      stateProvince: abbrev,
      postalCode: abbrev.length === 2
        ? `${Math.floor(Math.random() * 99999).toString().padStart(5, '0')}`
        : `${abbrev[0]}${Math.floor(Math.random() * 9)}${abbrev[1] || 'A'} ${Math.floor(Math.random() * 9)}${String.fromCharCode(65 + Math.floor(Math.random() * 26))}${Math.floor(Math.random() * 9)}`,
      region,
      dealerSince: `${yearJoined}-${String(Math.floor(Math.random() * 12) + 1).padStart(2, '0')}-01`,
      revenue: {
        current: Math.round(baseRevenue),
        DoD: +(0.4 + Math.random() * 1.5).toFixed(1),
        WoW: +(0.9 + Math.random() * 2.5).toFixed(1),
        MoM: +(1.8 + Math.random() * 3.5).toFixed(1),
        QoQ: +(3.0 + Math.random() * 4.5).toFixed(1),
        YoY: +(5.0 + Math.random() * 7).toFixed(1),
      },
      unitsInstalled: {
        current: Math.round(200 + Math.random() * 1200),
        DoD: +(0.5 + Math.random() * 1.5).toFixed(1),
        WoW: +(1.0 + Math.random() * 2.5).toFixed(1),
        MoM: +(2.0 + Math.random() * 3.5).toFixed(1),
        QoQ: +(3.5 + Math.random() * 5).toFixed(1),
        YoY: +(6.0 + Math.random() * 8).toFixed(1),
      },
      avgOrderValue: {
        current: Math.round(1500 + Math.random() * 2500),
        DoD: +(0.1 + Math.random() * 0.4).toFixed(1),
        WoW: +(0.3 + Math.random() * 0.7).toFixed(1),
        MoM: +(0.5 + Math.random() * 1.2).toFixed(1),
        QoQ: +(1.0 + Math.random() * 1.8).toFixed(1),
        YoY: +(2.0 + Math.random() * 3).toFixed(1),
      },
      otifRate: +(92 + Math.random() * 7).toFixed(1),
      customerSatisfaction: +(4.0 + Math.random() * 0.9).toFixed(1),
      certificationLevel: certLevels[Math.floor(i / (count / 4)) % 4],
      isTopPerformer: isTop,
      isBottomPerformer: isBottom,
    });
  }

  return dealers;
};

// Regional configurations
const bcCities = ['Vancouver', 'Surrey', 'Burnaby', 'Victoria', 'Kelowna', 'Kamloops', 'Abbotsford', 'Langley', 'Nanaimo', 'Chilliwack'];
const abCities = ['Calgary', 'Edmonton', 'Red Deer', 'Lethbridge', 'Medicine Hat', 'Airdrie', 'Spruce Grove'];
const onCities = ['Toronto', 'Ottawa', 'Mississauga', 'Hamilton', 'London', 'Kitchener', 'Burlington', 'Oakville', 'Barrie', 'Markham'];
const usPacificCities = ['Seattle', 'Portland', 'San Francisco', 'Los Angeles', 'San Diego', 'Sacramento', 'Fresno', 'Spokane', 'Tacoma', 'Eugene'];
const usMountainCities = ['Denver', 'Phoenix', 'Salt Lake City', 'Boise', 'Albuquerque', 'Tucson', 'Las Vegas', 'Colorado Springs'];
const otherCities = ['Halifax', 'Winnipeg', 'Saskatoon', 'Regina', 'Montreal', 'Quebec City'];

export const PHANTOM_REGIONS: RegionalData[] = [
  {
    region: 'British Columbia',
    abbreviation: 'BC',
    dealerCount: 45,
    revenue: { current: 18500000, DoD: 1.2, WoW: 2.4, MoM: 3.8, QoQ: 5.9, YoY: 9.2 },
    unitsInstalled: { current: 9800, DoD: 1.1, WoW: 2.2, MoM: 3.5, QoQ: 5.5, YoY: 8.8 },
    otifRate: 95.2,
    dealers: generateDealers('British Columbia', 'BC', bcCities, 45),
  },
  {
    region: 'Alberta',
    abbreviation: 'AB',
    dealerCount: 38,
    revenue: { current: 14200000, DoD: 0.8, WoW: 1.9, MoM: 2.9, QoQ: 4.5, YoY: 7.1 },
    unitsInstalled: { current: 7600, DoD: 0.7, WoW: 1.7, MoM: 2.6, QoQ: 4.2, YoY: 6.8 },
    otifRate: 94.8,
    dealers: generateDealers('Alberta', 'AB', abCities, 38),
  },
  {
    region: 'Ontario',
    abbreviation: 'ON',
    dealerCount: 62,
    revenue: { current: 24800000, DoD: 0.9, WoW: 1.7, MoM: 2.6, QoQ: 4.2, YoY: 6.8 },
    unitsInstalled: { current: 13200, DoD: 0.8, WoW: 1.6, MoM: 2.4, QoQ: 3.9, YoY: 6.4 },
    otifRate: 93.5,
    dealers: generateDealers('Ontario', 'ON', onCities, 62),
  },
  {
    region: 'US Pacific',
    abbreviation: 'PAC',
    dealerCount: 52,
    revenue: { current: 19200000, DoD: 1.1, WoW: 2.1, MoM: 3.2, QoQ: 5.1, YoY: 8.4 },
    unitsInstalled: { current: 10400, DoD: 1.0, WoW: 2.0, MoM: 3.0, QoQ: 4.8, YoY: 7.9 },
    otifRate: 94.1,
    dealers: generateDealers('US Pacific', 'PAC', usPacificCities, 52),
  },
  {
    region: 'US Mountain',
    abbreviation: 'MTN',
    dealerCount: 28,
    revenue: { current: 8100000, DoD: 0.7, WoW: 1.5, MoM: 2.4, QoQ: 3.8, YoY: 6.2 },
    unitsInstalled: { current: 4300, DoD: 0.6, WoW: 1.3, MoM: 2.2, QoQ: 3.5, YoY: 5.8 },
    otifRate: 94.6,
    dealers: generateDealers('US Mountain', 'MTN', usMountainCities, 28),
  },
  {
    region: 'Other',
    abbreviation: 'OTH',
    dealerCount: 20,
    revenue: { current: 4200000, DoD: 0.6, WoW: 1.2, MoM: 2.1, QoQ: 3.2, YoY: 5.4 },
    unitsInstalled: { current: 2930, DoD: 0.5, WoW: 1.0, MoM: 1.8, QoQ: 2.9, YoY: 4.8 },
    otifRate: 93.8,
    dealers: generateDealers('Other', 'OTH', otherCities, 20),
  },
];

// Aggregate metrics
export const PHANTOM_METRICS = {
  totalDealers: 245,
  totalRevenue: { current: 89000000, DoD: 0.9, WoW: 1.8, MoM: 2.9, QoQ: 4.5, YoY: 7.2 },
  ebitda: { current: 15130000, DoD: 0.7, WoW: 1.5, MoM: 2.4, QoQ: 3.8, YoY: 6.5 },
  unitsShipped: { current: 48230, DoD: 1.1, WoW: 2.2, MoM: 3.5, QoQ: 5.8, YoY: 9.1 },
  otif: { current: 94.2, DoD: 0.2, WoW: 0.4, MoM: 0.8, QoQ: 1.2, YoY: 2.1 },
  fillRate: { current: 97.8, DoD: 0.1, WoW: 0.2, MoM: 0.3, QoQ: 0.5, YoY: 0.9 },
  capacityUtilization: { current: 82.5, DoD: 0.5, WoW: 1.2, MoM: 2.1, QoQ: 3.5, YoY: 5.8 },
  firstPassYield: { current: 96.4, DoD: 0.1, WoW: 0.2, MoM: 0.4, QoQ: 0.6, YoY: 1.2 },
  leadTimeDays: { current: 8.2, DoD: -0.1, WoW: -0.3, MoM: -0.5, QoQ: -0.8, YoY: -1.5 },
  warrantyRate: { current: 0.8, DoD: 0.0, WoW: -0.1, MoM: -0.1, QoQ: -0.2, YoY: -0.3 },
};

// Product lines
export const PHANTOM_PRODUCTS = [
  {
    name: 'Retractable Door Screens',
    units: { current: 18230, DoD: 1.0, WoW: 2.1, MoM: 3.4, QoQ: 5.5, YoY: 8.8 },
    revenue: { current: 28200000, DoD: 1.0, WoW: 2.1, MoM: 3.4, QoQ: 5.5, YoY: 8.8 },
    margin: 18.2,
  },
  {
    name: 'Retractable Window Screens',
    units: { current: 14560, DoD: 0.8, WoW: 1.7, MoM: 2.8, QoQ: 4.3, YoY: 7.2 },
    revenue: { current: 19400000, DoD: 0.8, WoW: 1.7, MoM: 2.8, QoQ: 4.3, YoY: 7.2 },
    margin: 17.5,
  },
  {
    name: 'Motorized Screens',
    units: { current: 8920, DoD: 1.4, WoW: 2.8, MoM: 4.2, QoQ: 6.8, YoY: 12.5 },
    revenue: { current: 24800000, DoD: 1.4, WoW: 2.8, MoM: 4.2, QoQ: 6.8, YoY: 12.5 },
    margin: 19.8,
  },
  {
    name: 'Commercial Systems',
    units: { current: 6520, DoD: 0.6, WoW: 1.4, MoM: 2.2, QoQ: 3.5, YoY: 5.8 },
    revenue: { current: 16600000, DoD: 0.6, WoW: 1.4, MoM: 2.2, QoQ: 3.5, YoY: 5.8 },
    margin: 16.2,
  },
];

// Top performers
export const getTopDealers = (count: number = 5): Dealer[] => {
  const allDealers = PHANTOM_REGIONS.flatMap(r => r.dealers);
  return allDealers
    .sort((a, b) => b.revenue.current - a.revenue.current)
    .slice(0, count);
};

// Bottom performers
export const getBottomDealers = (count: number = 5): Dealer[] => {
  const allDealers = PHANTOM_REGIONS.flatMap(r => r.dealers);
  return allDealers
    .sort((a, b) => a.revenue.current - b.revenue.current)
    .slice(0, count);
};

// Company history
export const PHANTOM_HISTORY = {
  founded: 1992,
  founder: 'Co-founded with Rooke family involvement',
  headquarters: 'Abbotsford, BC',
  rookeConnection: 'Co-founding partner, ongoing ownership stake',
  milestones: [
    { year: 1992, event: 'Founded in Abbotsford, BC - first retractable screen manufacturer in Canada' },
    { year: 1996, event: 'Expanded dealer network to 25 locations' },
    { year: 2000, event: 'Entered US Pacific Northwest market' },
    { year: 2005, event: 'Launched motorized screen product line' },
    { year: 2008, event: 'Reached 100 dealers across North America' },
    { year: 2012, event: 'Introduced commercial screen systems division' },
    { year: 2015, event: 'Opened second manufacturing facility' },
    { year: 2018, event: 'Launched smart home integration features' },
    { year: 2022, event: 'Reached 245 dealers, 30th anniversary celebration' },
  ],
};
